import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import {
  css,
  html,
  LitElement,
  nothing,
  PropertyValueMap,
  TemplateResult,
  unsafeCSS,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { ProductionSpeedOptionDto, ProductionSpeedType, Step } from '@/api-sdk';
import CheckUrl from '@/assets/svg/check.svg?raw';
import { iconCircleInfo, iconStopwatch } from '@/components/icons';
import { global, GlobalComponentType } from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, rootVariablesStyles, typographyStyles } from '@/styles';
import { LayoutType } from '@/types';
import {
  afterCutoffTime,
  formatDate,
  formatTimeString,
  svgUriEncoder,
} from '@/utils';

import '../fg-configurator-step.ts';

@customElement('fg-configurator-step-production-speed')
@localized()
export class FgConfigurator extends LitElement {
  /**
   * Array of possible production options in the configurator
   */
  @property({ type: Array })
  options!: Array<ProductionSpeedOptionDto>;

  /**
   * Store the current selected production speed info to render the cutoff time info string that is rendered outside of
   * the options loop.
   */
  @state()
  selectedProductionSpeedInfo?: {
    cutoffTime: string;
    timeInDays: number;
  };

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  protected willUpdate(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    if (_changedProperties.has('options')) {
      const selectedValue = this.options.findIndex(option => option.selected);
      const cutoffTime = this.options[selectedValue]?.cutoffTime;
      const { layout } = this.globalContext;
      const timeInDays =
        layout === 'retail'
          ? this.options[selectedValue]?.deliveryTimeInDays
          : this.options[selectedValue]?.shippingTimeInDays;

      if (!cutoffTime || !timeInDays) {
        this.selectedProductionSpeedInfo = undefined;
        return;
      }

      this.selectedProductionSpeedInfo = {
        cutoffTime: cutoffTime,
        timeInDays: timeInDays,
      };
    }
  }

  renderInWorkingDays(count: number | null | undefined, layout: LayoutType) {
    if (typeof count !== 'number') {
      return nothing;
    }

    const inWorkingDaysTranslations = {
      retail: 'content.productionSpeed.deliveryInWorkingDays',
      wholesale: 'content.productionSpeed.shippingInWorkingDays',
    };

    return html`
      <span class="production-speed-working-days">
        ${t(inWorkingDaysTranslations[layout], {
          count: count,
        })}
      </span>
    `;
  }

  renderExpectedDate(date: string | null | undefined, layout: LayoutType) {
    const expectedDateTranslation = {
        retail: 'content.configurator.expectedDeliveryDate',
        wholesale: 'content.configurator.expectedShippingDate',
      },
      { locale } = this.globalContext;

    return date
      ? html`
          ${t(expectedDateTranslation[layout])}
          <strong>
            ${formatDate(date, locale, layout === 'retail' ? 'long' : 'short')}
          </strong>
        `
      : nothing;
  }

  renderCutoffTimeInfo(): TemplateResult | typeof nothing {
    if (
      !this.selectedProductionSpeedInfo?.cutoffTime &&
      typeof this.selectedProductionSpeedInfo?.timeInDays !== 'number'
    ) {
      return nothing;
    }
    const cutoffDay = {
        today: 'content.productionSpeed.cutoffInfo',
        tomorrow: 'content.productionSpeed.cutoffInfoTomorrow',
        todayWholesale: 'content.productionSpeed.cutoffInfoWholesale',
        tomorrowWholesale:
          'content.productionSpeed.cutoffInfoWholesaleTomorrow',
      },
      { layout, locale } = this.globalContext;

    return html`
      <div class="cutoff-time-info">
        ${iconCircleInfo}
        <span>
          ${unsafeHTML(
            t(
              cutoffDay[
                afterCutoffTime(this.selectedProductionSpeedInfo?.cutoffTime)
                  ? layout === 'retail'
                    ? 'tomorrow'
                    : 'tomorrowWholesale'
                  : layout === 'retail'
                    ? 'today'
                    : 'todayWholesale'
              ],
              {
                count: this.selectedProductionSpeedInfo.timeInDays,
                time: formatTimeString(
                  this.selectedProductionSpeedInfo.cutoffTime,
                  locale,
                ),
              },
            ),
          )}
        </span>
      </div>
    `;
  }

  handleProductionSpeedSelect(queryString?: string | null) {
    if (!queryString) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString: queryString,
          },
        },
      }),
    );
  }

  render() {
    if (!this.options || this.options.length === 0) {
      return nothing;
    }
    const { layout } = this.globalContext;

    return html`
      <fg-configurator-step
        part=${layout}
        .stepTitle=${layout === 'retail'
          ? t('headers.chooseDeliverySpeed')
          : null}
      >
        ${layout === 'wholesale'
          ? html`
              <fg-step-header
                part=${layout}
                slot="step-header"
                .stepType=${Step.PRODUCTION_SPEED}
              ></fg-step-header>
            `
          : nothing}
        <div class="production-speed-wrapper">
          ${this.options.map(
            option => html`
              <label
                for=${option.code}
                class=${classMap({
                  discount: option.discount !== null,
                  [option.code]: option.code,
                  selected: option.selected,
                  disabled: !option.enabled,
                })}
                @click=${() => {
                  if (!option.selected && option.enabled) {
                    this.handleProductionSpeedSelect(option.queryString);
                  }
                }}
              >
                <div>
                  <input
                    ?checked=${option.selected}
                    id=${option.code}
                    ?disabled=${!option.enabled}
                    name="production-speed"
                    type="radio"
                  />
                </div>
                <div>
                  <h3>
                    ${option.code === ProductionSpeedType.HSP
                      ? html`
                          <span>
                            ${iconStopwatch}
                            ${layout === 'retail'
                              ? t('content.productionSpeed.quickDelivery')
                              : t('content.productionSpeed.24hProduction')}
                            <span></span>
                          </span>
                        `
                      : layout === 'retail'
                        ? t('content.productionSpeed.standardDelivery')
                        : t('content.productionSpeed.standardProduction')}
                  </h3>

                  ${option.enabled
                    ? html`
                        ${this.renderInWorkingDays(
                          layout === 'retail'
                            ? option.deliveryTimeInDays
                            : option.shippingTimeInDays,
                          layout,
                        )}
                        ${this.renderExpectedDate(
                          layout === 'retail'
                            ? option.expectedDeliveryDate
                            : option.expectedShippingDate,
                          layout,
                        )}
                      `
                    : nothing}
                  ${option.disabledReason && !option.enabled
                    ? html`
                        <span>${option.disabledReason}</span>
                      `
                    : nothing}
                </div>
                ${option.discount && option.enabled
                  ? html`
                      <div class="discount-wrapper">
                        <strong>- ${option.discount}</strong>
                        <span>${t('content.productionSpeed.discount')}</span>
                      </div>
                    `
                  : nothing}
              </label>
            `,
          )}
        </div>
        ${this.renderCutoffTimeInfo()}
      </fg-configurator-step>
    `;
  }
  static styles = [
    baseStyles,
    typographyStyles,
    rootVariablesStyles,
    css`
      .production-speed-wrapper {
        display: flex;
        gap: 0.375rem;
        margin-top: 1rem;

        @media screen and (max-width: ${BREAKPOINT_LARGE_PX - 1}px) {
          flex-direction: column;
        }

        @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
          gap: 0.625rem;
        }

        & label {
          display: grid;
          grid-template-columns: max-content auto;
          column-gap: 0.5rem;
          border-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
          padding: 1rem;
          background-color: #ffffff;
          cursor: pointer;
          position: relative;
          overflow: hidden;

          @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
            flex-basis: 50%;
          }

          > div {
            display: flex;
            flex-direction: column;
          }

          &.discount {
            grid-template-columns: max-content auto max-content;

            & .discount-wrapper {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              color: var(--fg-message-success);
            }

            & .discount-wrapper strong {
              font-size: 1.125rem;
            }

            & .discount-wrapper span {
              font-size: 0.75rem;
            }
          }

          &.selected {
            background-color: var(
              --fg-config-selected--light,
              var(--fg-fallback-config-selected--light)
            );
          }

          &.disabled,
          &.disabled input[type='radio'] {
            cursor: default;
          }

          &.disabled::after {
            content: '';
            position: absolute;
            inset: 0;
            background-color: rgba(255, 255, 255, 0.5);
          }
        }
      }

      h3 {
        margin-bottom: 4px;
        & span {
          line-height: 1.25rem;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }

      .production-speed-working-days {
        display: block;
        margin-bottom: 1rem;
      }

      .production-speed-working-days + span {
        display: flex;
        flex-direction: column;
        margin-top: auto;
      }

      input[type='radio'] {
        appearance: none;
        margin: 0;
        padding: 0;
        border-radius: 50%;
        cursor: pointer;
      }

      .hsp {
        order: -1;
      }

      .cutoff-time-info {
        display: flex;
        margin-top: 0.625rem;
        gap: 0.25rem;

        & svg {
          display: block;
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          color: var(--fg-primary, var(--fg-fallback-primary));
        }
      }

      fg-configurator-step[part='wholesale'] {
        & .production-speed-wrapper label {
          border-radius: var(
            --fg-borderradius--sm,
            var(--fg-fallback-borderradius--sm)
          );
          background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));

          & .discount-wrapper strong {
            font-size: 1.375rem;
            color: var(--fg-success--dark, var(--fg-fallback-success--dark));
          }

          &.selected {
            background-color: var(--fg-success--light, var(--fg-success--dark));
            border: 1px solid
              var(--fg-success--dark, var(--fg-fallback-success--dark));
          }

          & h3,
          & h3 span {
            font-size: 1rem;
            line-height: 1.5rem;
          }

          input[type='radio'] {
            border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray-300));
            background-color: #ffffff;
            width: 24px;
            height: 24px;
            &[checked] {
              border-color: var(--fg-success, var(--fg-fallback-success));
              background-color: var(--fg-success, var(--fg-fallback-success));
              background-image: url(${unsafeCSS(svgUriEncoder(CheckUrl))});
              background-repeat: no-repeat;
              background-position: center;
              background-size: 16px 16px;
            }
          }

          .production-speed-working-days,
          .production-speed-working-days + span {
            font-size: 0.875rem;
          }
        }

        .cutoff-time-info svg {
          color: var(--fg-secondary, var(--fg-fallback-secondary));
        }
      }

      fg-configurator-step[part='retail'] {
        .production-speed-wrapper label {
          border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));

          &.selected {
            border: 2px solid
              var(--fg-config-selected, var(--fg-fallback-config-selected));
          }
        }

        .production-speed-wrapper label:hover:not(.selected):not(.disabled) {
          border-color: var(--fg-primary, var(--fg-fallback-primary));
          box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        }

        input[type='radio'] {
          border: 2px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
          width: 20px;
          height: 20px;

          &[checked] {
            border: 6px solid
              var(--fg-config-selected, var(--fg-fallback-config-selected));
          }
        }
      }
    `,
  ];
}
